/* eslint-disable react/forbid-component-props */
import PropTypes from 'prop-types';
import React, { useContext } from 'react';
import {
  KLARNA_LOGO,
  KLARNA_MARKETING_BADGE,
} from '../../config/constants/images';
import { KLARNA_MARKETING_BADGE_TEXT } from '../../config/text/text';
import { isIESite } from '../../helpers/pages';
import ThemeContext from '../../providers/ThemeProvider';
import WowModal from '../WowModal';
import CTAButton from '../_generic/button/CTAButton';
import KlarnaModalList from './KlarnaModalList';

const KlarnaModal = ({
  currentRef,
  price,
  setShowKlarnaModal,
  showKlarnaModal,
}) => {
  const theme = useContext(ThemeContext);
  const closeModal = () => {
    setShowKlarnaModal(false);
  };
  const isIrelandSite = isIESite();
  const overThirtyFive = price > 35;

  return (
    <>
      <WowModal
        ariaLabelledBy=""
        centered
        container={currentRef}
        extraClasses="range-modal"
        setShowModal={setShowKlarnaModal}
        showModal={showKlarnaModal}
        size="md"
        title="Klarna."
        titleImage={KLARNA_LOGO}
        titleImageAlt="Klarna Logo"
      >
        <div className="modal-container" data-testid="klarna-modal-container">
          <h1 className="header-text">
            {overThirtyFive
              ? `3 payments of ${isIrelandSite ? '€' : '£'}
            ${(Math.floor((price / 3) * 100) / 100).toFixed(2)}.`
              : `Pay ${isIrelandSite ? '€' : '£'}${price} in 30 days`}
          </h1>

          <div className="subheader-text">
            {overThirtyFive ? (
              <p>Pay in 3 payments so you can spread the cost.</p>
            ) : (
              <p>Get 30 days to pay. No impact on your credit score.</p>
            )}
          </div>

          <div className="timeline">
            <KlarnaModalList message={`Add item(s) to your cart.`} />

            <KlarnaModalList
              image={KLARNA_MARKETING_BADGE}
              imageAlt={KLARNA_MARKETING_BADGE_TEXT}
              message={`Go to checkout and choose `}
            />
            {overThirtyFive ? (
              <KlarnaModalList
                message={`Enter your debit or credit information.`}
              />
            ) : (
              <KlarnaModalList
                message={`No card details required to checkout. Klarna will send you payment instructions once your order is completed.`}
              />
            )}
            {overThirtyFive ? (
              <KlarnaModalList
                message={`To check your eligibility, Klarna will perform a soft search with
                a credit reference agency. This will not affect your credit score.`}
              />
            ) : (
              <KlarnaModalList
                message={`Your payment period starts when your order is shipped.`}
              />
            )}
            {overThirtyFive ? (
              <KlarnaModalList
                last
                message={`Spread the cost in 3 payments. The first payments is
              made at the point of purchase, with the remaining 2 payments
              scheduled automatically every 30 days.`}
              />
            ) : (
              <KlarnaModalList
                last
                message={`You can easily make or schedule your payment online or in the Klarna app at any time up to 30 days. Pay securely by connecting your bank account, debit or credit card.`}
              />
            )}
          </div>
          <div className="klarna-footer">
            Klarna&apos;s Pay in 3 / Pay in 30 days are unregulated credit
            agreements. Borrowing more than you can afford or paying late may
            negatively impact your financial status and ability to obtain
            credit. 18+, UK residents only. Subject to status. Late fees may
            apply. Ts&amp;Cs{' '}
            <a
              className={'klarna-link'}
              href={'https://www.klarna.com/uk/terms-and-conditions/'}
              rel="noreferrer"
              target="_blank"
            >
              apply
            </a>
            .
          </div>
          <div className="confirmation-button">
            <CTAButton className="klarna-button" onClick={closeModal}>
              {`Close`}
            </CTAButton>
          </div>
        </div>
      </WowModal>
      <style jsx>{`
        .modal-container {
          padding: 0 30px;
        }
        @media (max-width: ${theme.breakpoints.smDown}) {
          .modal-container {
            padding: 0 10px;
          }
        }
        .header-text {
          -webkit-font-smoothing: antialiased;
          color: #171717;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
            Arial, sans-serif;
          font-size: 27px;
          font-weight: 700;
          letter-spacing: 0.3px;
          line-height: 30px;
          margin: -5px 0px -10px;
          padding: 1px 0px 20px;
        }
        .subheader-text {
          color: #171717;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
            Arial, sans-serif;
          font-size: 16px;
          font-weight: 300;
          letter-spacing: 0px;
          line-height: 25px;
          margin-bottom: -10px;
          margin-top: -20px;
          padding-bottom: 3px;
          padding-top: 2px;
        }
        .klarna-footer {
          -webkit-font-smoothing: antialiased;
          color: #787573;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
            Arial, sans-serif;
          font-size: 12px;
          font-weight: 400;
          letter-spacing: 0px;
          line-height: 20px;
          max-width: 100%;
          padding-bottom: 30px;
          padding-top: 15px;
        }
        .klarna-link {
          -webkit-font-smoothing: antialiased;
          border-radius: 2px;
          color: #787573;
          cursor: pointer;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
            Arial, sans-serif;
          font-weight: 400;
          outline: 0px;
          text-decoration: underline #787573;
          text-rendering: geometricprecision;
          transition: color 0.2s ease 0s;
        }
      `}</style>
    </>
  );
};

KlarnaModal.propTypes = {
  currentRef: PropTypes.object,
  price: PropTypes.number,
  setShowKlarnaModal: PropTypes.func,
  showKlarnaModal: PropTypes.bool,
};

export default KlarnaModal;
